import React, { ChangeEvent, Component, FormEvent } from 'react';
import { connect } from 'react-redux';
import { ConnectedProps } from 'react-redux/es/exports';
import { query, repository, subscribe } from '@trlogic/client-js';
import { InstanceContainer } from '../../../config/binding/InstanceBinding';
import Injectables from 'constant/Injectables';
import FormicaLabel from '../../custom/FormicaLabel';
import { FormicaProps, withFormica } from '../../../helpers/withFormica';
import { ActionJsonModel, ApiActionJsonModel, ApiActionVariableParameter } from '../../../domain/collection/rule/action/ActionDomains';
import { TransactionJsonModel } from '../../../domain/stream/transaction/TransactionDomains';
import { RootState } from '../../../redux/store';
import { ActionState } from '../../../redux/reducers/ActionReducer';
import { changeAction, changeActionIsUpdate, clearAction } from '../../../redux/actions/ActionActions';
import { enterComponent, leaveComponent } from '../../../redux/actions/ComponentActions';
import CustomLoading from '../../custom/CustomLoading';
import RoleHelper from '../../../RoleHelper';
import FormicaSearchSelectBox, { FormicaSelectBoxItem } from '../../custom/FormicaSearchSelectBox';
import FormicaInput from '../../custom/FormicaInput';
import CustomTabv2, { CustomTabItem } from '../../custom/CustomTabv2';
import CollectionNames from '../../../constant/CollectionNames';
import { requireNonUndefined } from '../../../util/Assert';
import HttpMethod from '../../../constant/HttpMethod';
import ApiParametersComponent from './editor/parameters/ApiParametersComponent';
import ApiAuthorizationComponent from './editor/auth/ApiAuthorizationComponent';
import ApiHeadersComponent from './editor/headers/ApiHeadersComponent';
import ApiBodyComponent from './editor/body/ApiBodyComponent';
import LanguageUtil from '../../../util/LanguageUtil';
import ComponentName from '../../../constant/ComponentName';

class ActionApiEditorComponent extends Component<Props, State> {
  public state = new State();

  private transactionSubscription: subscribe.Subscription | undefined;
  private computation: subscribe.Computation | undefined;

  private readonly repository: repository.ReadOnlyQueryableRepository = InstanceContainer.get(Injectables.REPOSITORY);

  public render() {
    const {
      action: { action },
      component: { module },
      user,
    } = this.props;
    const { loading } = this.state;
    const isKyc = module.includes('kyc');

    if (loading) {
      return <CustomLoading />;
    }

    return (
      <div className={'grid grid-cols-8 w-full !max-h-[calc(100vh-15rem)] overflow-y-scroll px-5'}>
        <div className={'col-span-2 flex flex-col border-r border-f-black-5 pr-5 py-5 gap-y-3'}>
          <span className={'flex bg-[#F7F8FA] rounded-[4px] w-full justify-between gap-x-1 px-1 py-1 h-[36px]'}>
            <div
              onClick={() => this.onSelectType('transaction')}
              className={`${action.type == 'transaction' ? 'bg-white opacity-100' : 'opacity-50'} w-full cursor-pointer text-f-black-1 px-1 py-1 rounded-[2px] flex items-center justify-center`}
            >
              {LanguageUtil.get({ id: 'pages.action.api.transaction', defaultMessage: 'Transaction' })}
            </div>
            {isKyc && user.activeRiskType == 'compliance' && (
              <div
                onClick={() => this.onSelectType('kyc')}
                className={`${action.type == 'kyc' ? 'bg-white opacity-100' : 'opacity-50'} w-full cursor-pointer text-f-black-1 px-1 py-1 rounded-[2px] flex items-center justify-center`}
              >
                {LanguageUtil.get({ id: 'pages.action.api.kyc', defaultMessage: 'KYC' })}
              </div>
            )}
          </span>
          <div className={'flex flex-col'}>
            <FormicaLabel label={LanguageUtil.get({ id: 'common.label.channel', defaultMessage: 'Channel' })} />
            <FormicaSearchSelectBox
              options={this.getTransactionOptions()}
              rounded
              placeholder={LanguageUtil.get({ id: 'common.input.placeholder.channel', defaultMessage: 'Channel' })}
              disabled={!RoleHelper.canEdit('Action')}
              value={action.transaction}
              onChange={(e: FormicaSelectBoxItem) => {
                this.onChangeTransaction(e);
              }}
              data-testid={`${ComponentName.ACTION}_channel_select`}
            />
          </div>
          <div className={'w-full'}>
            <FormicaLabel label={LanguageUtil.get({ id: 'common.label.timeout', defaultMessage: 'Timeout (ms)' })} />
            <FormicaInput
              disabled={!RoleHelper.canEdit('Action')}
              type={'number'}
              onChange={(e: FormEvent<HTMLInputElement>) => this.onChangeTimeout(e)}
              value={action.timeout}
              placeholder={LanguageUtil.get({ id: 'common.input.placeholder.timeout', defaultMessage: 'Enter timeout in milliseconds' })}
              data-testid={`${ComponentName.ACTION}_timeout_input`}
            />
          </div>
        </div>
        <div className={'col-span-6 flex flex-col w-full h-full px-5 py-5 gap-y-4'}>
          <div className={'flex gap-x-2'}>
            <div className={'flex w-[120px]'}>
              <FormicaSearchSelectBox
                disabled={!RoleHelper.canEdit('Action')}
                placeholder={LanguageUtil.get({ id: 'common.input.placeholder.method', defaultMessage: 'Method' })}
                value={(action as ApiActionJsonModel).method}
                options={this.getMethodTypes()}
                onChange={(e: FormicaSelectBoxItem) => this.onChangeMethod(e)}
                data-testid={`${ComponentName.ACTION}_method_select`}
              />
            </div>
            <FormicaInput
              disabled={!RoleHelper.canEdit('Action')}
              value={(action as ApiActionJsonModel).url}
              onChange={(e: ChangeEvent<HTMLInputElement>) => this.onChangeUrl(e)}
              placeholder={LanguageUtil.get({ id: 'common.input.placeholder.url', defaultMessage: 'Url' })}
              data-testid={`${ComponentName.ACTION}_url_input`}
            />
          </div>
          <CustomTabv2 isContainer={false} tab={this.getTabs()} />
        </div>
      </div>
    );
  }

  public async componentDidMount() {
    await this.startTracker();
  }

  public async componentWillUnmount() {
    await this.stopTracker();
  }

  private startTracker = async () => {
    const transactionQuery = query.QueryBuilder.select().from(CollectionNames.TRANSACTIONS).select('id', 'name');
    this.transactionSubscription = subscribe.Formica.subscribe(CollectionNames.TRANSACTIONS, transactionQuery.build());
    this.computation = subscribe.Tracker.autorun(this.tracker, [this.transactionSubscription]);
  };

  private tracker = async () => {
    if (this.transactionSubscription?.ready()) {
      const transactionQuery: query.Query = query.QueryBuilder.select().from(CollectionNames.TRANSACTIONS).build();
      const transactions: string[] = (await this.repository.findMany<TransactionJsonModel>(transactionQuery)).flatMap((a) => a.name);
      this.setState({ transactions, loading: false });
    }
  };

  private stopTracker = async () => {
    await this.transactionSubscription?.stop();
    this.computation?.stop();
  };

  private getMethodTypes = () => {
    return [
      {
        label: LanguageUtil.get({ id: 'pages.action.api.methods.get', defaultMessage: 'GET' }),
        value: HttpMethod.GET,
      },
      {
        label: LanguageUtil.get({ id: 'pages.action.api.methods.post', defaultMessage: 'POST' }),
        value: HttpMethod.POST,
      },
      {
        label: LanguageUtil.get({ id: 'pages.action.api.methods.put', defaultMessage: 'PUT' }),
        value: HttpMethod.PUT,
      },
      {
        label: LanguageUtil.get({ id: 'pages.action.api.methods.delete', defaultMessage: 'DELETE' }),
        value: HttpMethod.DELETE,
      },
      {
        label: LanguageUtil.get({ id: 'pages.action.api.methods.patch', defaultMessage: 'PATCH' }),
        value: HttpMethod.PATCH,
      },
    ];
  };

  private getUniqueListBy(arr: any[], key: string) {
    return [...new Map(arr.map((item) => [item[key], item])).values()];
  }

  private getKeyAndValues(url: string) {
    const regex = /[?&]([^=#]+)=([^&#]*)/g;
    let urlParams = url.split(regex);
    urlParams = urlParams.filter((item) => item != '');
    urlParams.shift();
    return urlParams;
  }

  private addNewParameter(url: string[]) {
    const {
      action: { action },
      changeAction,
    } = this.props;
    for (let i = 0; i < url.length; i++) {
      if (url.length % 2 == 0) {
        const parameter: ApiActionVariableParameter = {
          key: url[i],
          value: url[i + 1],
          description: '',
          isActive: true,
        };
        (action as ApiActionJsonModel).parameters = this.getUniqueListBy([...(action as ApiActionJsonModel).parameters, parameter], 'key');
        i++;
      }
    }
    changeAction(action);
  }

  private onChangeUrl(e: ChangeEvent<HTMLInputElement>) {
    const {
      action: { action },
      changeAction,
    } = this.props;
    const oldUrl = this.getKeyAndValues((action as ApiActionJsonModel).url || 'url');
    (action as ApiActionJsonModel).url = e.currentTarget.value;
    const url = this.getKeyAndValues((action as ApiActionJsonModel).url || 'url');
    oldUrl?.map((item, index) => {
      if (item != url[index]) {
        (action as ApiActionJsonModel).parameters = (action as ApiActionJsonModel).parameters?.filter((param) => param.key != item);
      }
    });
    changeAction(action);
    this.addNewParameter(url);
  }

  private onChangeTimeout = (e: any) => {
    const {
      action: { action },
      changeAction,
    } = this.props;
    action.timeout = parseInt(e.currentTarget.value);
    changeAction(action);
  };

  private onChangeTransaction(e: FormicaSelectBoxItem) {
    const {
      action: { action },
      changeAction,
    } = this.props;
    action.transaction = e.value;
    changeAction(action);
  }

  private getTransactionOptions() {
    const {
      action: { action },
    } = this.props;
    if (action.type == 'kyc') {
      return [
        {
          label: LanguageUtil.get({ id: 'common.label.individual', defaultMessage: 'Individual' }),
          value: 'individual',
        },
        { label: LanguageUtil.get({ id: 'common.label.entity', defaultMessage: 'Entity' }), value: 'entity' },
      ];
    } else {
      return this.state.transactions.map((transaction) => {
        return {
          label: transaction,
          value: transaction,
        };
      });
    }
  }

  private onChangeMethod(e: FormicaSelectBoxItem) {
    const {
      action: { action },
      changeAction,
    } = this.props;
    (action as ApiActionJsonModel).method = e.value as HttpMethod;
    changeAction(action);
  }

  private onSelectType(type: string) {
    const {
      action: { action },
      changeAction,
    } = this.props;
    action.type = type;
    changeAction(action);
  }

  private getTabs = () => {
    return [
      {
        key: 'Parameters',
        label: LanguageUtil.get({ id: 'common.label.parameters', defaultMessage: 'Parameters' }),
        content: <ApiParametersComponent />,
        testid: `${ComponentName.ACTION}_parameters_tab`,
      },
      {
        key: 'Authorization',
        label: LanguageUtil.get({ id: 'common.label.auth', defaultMessage: 'Authoization' }),
        content: <ApiAuthorizationComponent />,
        testid: `${ComponentName.ACTION}_auth_tab`,
      },
      {
        key: 'Headers',
        label: LanguageUtil.get({ id: 'common.label.headers', defaultMessage: 'Headers' }),
        content: <ApiHeadersComponent />,
        testid: `${ComponentName.ACTION}_headers_tab`,
      },
      {
        key: 'Body',
        label: LanguageUtil.get({ id: 'common.label.body', defaultMessage: 'Body' }),
        content: <ApiBodyComponent />,
        testid: `${ComponentName.ACTION}_body_tab`,
      },
    ];
  };
}

interface Props extends ReduxProps {}

interface Props extends ReduxProps, FormicaProps {}

class State {
  actions: ActionJsonModel[] = [];
  transactions: string[] = [];
  loading: boolean = true;
}

const mapStateToProps = (state: RootState) => ({
  action: state.action as ActionState,
  isUpdate: state.action.isUpdate,
  lastCategory: state.page.lastCategory,
  component: state.component,
  user: state.auth.user,
});
const mapDispatchToProps = {
  changeActionIsUpdate,
  changeAction,
  clearAction,
  enterComponent,
  leaveComponent,
};
const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export default withFormica(connector(ActionApiEditorComponent));
